import { CircleStop, Play } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { APPROVAL_ITEMS, PALETTE } from './meta'
import type { PaletteItem } from './model'

/**
 * The left column: every kind of step, each a card that appends one to the flow.
 *
 * <p>Start and End are listed as well, greyed, because they are on every flow and somebody
 * looking for "where does it begin" should find the answer here rather than wonder whether
 * they forgot to add it.
 */
export function StepPalette({ onAdd, trigger }: { onAdd: (item: PaletteItem) => void; trigger: string }) {
  return (
    <Card className="p-3">
      <h3 className="mb-2 text-sm font-semibold text-[var(--color-heading)]">Add Step</h3>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-muted)]">
        Approval steps
      </p>
      <ul className="space-y-2">
        {APPROVAL_ITEMS.map((item) => {
          const meta = PALETTE[item]
          const Icon = meta.icon
          return (
            <li key={item}>
              <button
                type="button"
                onClick={() => onAdd(item)}
                aria-label={`Add ${meta.label} step`}
                className="flex w-full items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-2 text-start transition hover:border-[var(--color-primary)] hover:bg-[var(--color-page)] focus-visible:outline-2 focus-visible:outline-[var(--color-primary)]"
              >
                <span className={`flex size-8 shrink-0 items-center justify-center rounded-md ${meta.tint}`}>
                  <Icon className={`size-4 ${meta.tone}`} aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-[var(--color-heading)]">{meta.label}</span>
                  <span className="block text-xs text-[var(--color-muted)]">{meta.hint}</span>
                </span>
              </button>
            </li>
          )
        })}
      </ul>
      <p className="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-muted)]">
        Flow controls
      </p>
      <ul className="space-y-2" aria-label="Fixed steps">
        <li className="flex items-start gap-2 rounded-md border border-dashed border-[var(--color-border)] p-2">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[var(--color-tint-green)]">
            <Play className="size-4 text-[var(--color-success)]" aria-hidden="true" />
          </span>
          <span>
            <span className="block text-sm font-medium text-[var(--color-heading)]">Start</span>
            <span className="block text-xs text-[var(--color-muted)]">{trigger} — always first</span>
          </span>
        </li>
        <li className="flex items-start gap-2 rounded-md border border-dashed border-[var(--color-border)] p-2">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[var(--color-page)]">
            <CircleStop className="size-4 text-[var(--color-muted)]" aria-hidden="true" />
          </span>
          <span>
            <span className="block text-sm font-medium text-[var(--color-heading)]">End</span>
            <span className="block text-xs text-[var(--color-muted)]">Approved once every step is done</span>
          </span>
        </li>
      </ul>
    </Card>
  )
}
